import type { Account } from '@/types/Models/Account'
import type { Company } from '@/types/Models/Company'
import { AbilityBuilder, createMongoAbility, type MongoAbility } from '@casl/ability'
import { CUSTOMER_ROLE_SHIPMENTS_VIEWER } from '@/const/AccountRoles'

type Actions = 'manage' | 'view' | 'create'
type Subjects =
  | 'staff.area'
  | 'prices'
  | 'proxy'
  | 'pickupDate'
  | 'pickups-list'
  | 'shipments'
  | 'statistics'
  | 'financial'
  | 'assignmentsList'
  | 'assignment'
  | 'customer'
  | 'single-parcel-assignment'
  | 'faq'
  | 'customer.area'
  | 'scan'
  | 'profile'
  | 'reports'
  | 'addressBook'
  | 'auditLogs'
  | 'inventory'

export type AppAbility = MongoAbility<[Actions, Subjects]>

export const defineAbilitiesFor = (account: Account | null, company: Company | null = null): AppAbility => {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)

  if (account && account.isScannerOnlyEnabled) {
    can('view', 'scan')
  } else if (account && account.type === 'staff') {
    can('manage', 'staff.area')
    can('manage', 'prices')
    can('manage', 'proxy')
    can('manage', 'pickupDate')
    can('view', 'scan')
    can('view', 'shipments')
    can('view', 'statistics')
    can('view', 'financial')
    can('view', 'pickups-list')
    can('view', 'assignmentsList')
    can('view', 'reports')
    can('create', 'assignment')
    can('create', 'customer')
    can('manage', 'assignment')
    can('manage', 'single-parcel-assignment')
    can('manage', 'customer')
    can('manage', 'faq')
    can('view', 'auditLogs')
    if (account.isInventoryAvailable) {
      can('view', 'inventory')
    }
  } else if (account && account.type === 'customer') {
    if (account.role === CUSTOMER_ROLE_SHIPMENTS_VIEWER) {
      can('view', 'shipments')
    } else {
      can('manage', 'customer.area')
      can('create', 'assignment')
      can('manage', 'assignment')
      can('manage', 'single-parcel-assignment')
      can('view', 'shipments')
      can('view', 'assignmentsList')
      can('view', 'statistics')
      can('view', 'profile')
      can('view', 'faq')
      can('view', 'reports')
      if (company?.isAddressBookAvailable) {
        can('view', 'addressBook')
      }
    }
  }

  return build()
}
